import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FolderCreateRequest } from '@ms-organization-entities';
import { NavigationData, REQUIRED_VALIDATOR_KEY } from '@pt/pt-models';
import { FolderService, LegalEntityService } from '@pt/api-client';
import { ActionsSubject, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { ofType } from '@ngrx/effects';
import * as FolderActions from '../documents/store/actions/folder.actions';
import * as DocumentsUtils from '../documents/utils/validator-generator';

@Component({
  selector: 'pt-create-folder-dialog',
  template: `
    <div class='modal-header'>
      <h4 class='modal-title'>
        {{ "TITLE.NEW_FOLDER" | translate }}
      </h4>
    </div>
    <div class='modal-body'>
      <form *ngIf='formGroup' [formGroup]='formGroup'>
        <div class='form-group'>
          <label class="form-label">{{ 'LABELS.NAME' | translate }}
            <span class='text-danger'>*</span>
          </label>
          <input type='text'
                 autocomplete='off'
                 formControlName='name'
                 class='form-control form-control-sm'
          >
        </div>

        <!--LEGAL ENTITY----------------------------------------------------------------->
        <ng-template #popContentLegalEntityContent class='p-0'>
          <pt-resource-navigation
            [title]="'TITLE.SELECT_LEGAL_ENTITY' | translate"
            [data]='legalEntity'
            [selectedResourceId]='selectedLegalEntity?.id'
            (clickClose)='popContentLegalEntity.close()'
            (clickItem)='selectLegalEntity($event)'>
          </pt-resource-navigation>
        </ng-template>

        <div class='tree-selector'>
          <label class="form-label">{{ 'LABELS.LEGAL_ENTITY' | translate }}
            <span class='text-danger' *ngIf='isRequiredLegalEntity'>*</span>
          </label>
          <div class='input-group input-group-sm mb-3'>
            <div class='input-group-text'>
              <pt-icon [key]="'legal-entity.svg'" [size]='14' [iconType]="'SVG'"></pt-icon>
            </div>
            <input type='text'
                   autocomplete='off'
                   formControlName='legalEntity'
                   class='form-control form-control-sm form-readonly--custom'
                   [ngbPopover]='popContentLegalEntityContent'
                   [autoClose]="'outside'"
                   #popContentLegalEntity='ngbPopover'
                   triggers='manual'
                   readonly
                   placement='top'
                   (click)='chooseLegalEntity()'
                   placeholder="{{'PLACEHOLDERS.LEGAL_ENTITY' | translate}}">
            <button type='button' class='btn-close tree-selector-clean font-10' aria-label='Close'
                    (click)='cleanLegalEntity()'>
              <!--              <span aria-hidden='true'>&times;</span>-->
            </button>
          </div>
        </div>

        <!--FOLDER----------------------------------------------------------------->
        <ng-template #popContentFolderContent class='p-0'>
          <pt-resource-navigation
            [title]="'TITLE.SELECT_FOLDER' | translate"
            [data]='folders'
            [selectedResourceId]='selectedFolder?.id'
            (clickClose)='popContentFolder.close()'
            (clickItem)='selectFolder($event)'>
          </pt-resource-navigation>
        </ng-template>

        <div class='tree-selector'>
          <label class="form-label">
            {{ 'LABELS.FOLDER' | translate }}
            <span class='text-danger'>*</span>
          </label>
          <div class='input-group input-group-sm mb-3'>
            <div class='input-group-text'>
              <pt-icon [key]="'folder.svg'" [size]='14' [iconType]="'SVG'"></pt-icon>
            </div>
            <input type='text'
                   autocomplete='off'
                   formControlName='folder'
                   class='form-control form-control-sm form-readonly--custom'
                   [ngbPopover]='popContentFolderContent'
                   [autoClose]="'outside'"
                   #popContentFolder='ngbPopover'
                   triggers='manual'
                   readonly
                   placement='top'
                   (click)='chooseFolder()'
                   placeholder="{{'PLACEHOLDERS.FOLDER' | translate}}">
            <button type='button' class='btn-close tree-selector-clean font-10' aria-label='Close'
                    (click)='cleanFolder()'>
              <!--              <span aria-hidden='true'>&times;</span>-->
            </button>
          </div>
        </div>
      </form>
    </div>

    <div class='modal-footer'>
      <button type='button' class='btn btn-primary btn-sm'
              [disabled]='formGroup?.invalid'
              (click)='confirm()'>
        {{ 'BUTTON.CREATE' | translate }}
      </button>
      <button ngbAutofocus type='button' class='btn btn-outline-secondary btn-sm'
              (click)='activeModal.close(false)'>
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  styles: [`
    .form-readonly--custom {
      background: white !important;
    }

    .tree-selector {
      margin-bottom: 16px;
    }

    .tree-selector-clean {
      position: absolute;
      right: 7px;
      font-size: 7px !important;
      top: 8px;
    }
  `]
})
export class CreateFolderDialogComponent implements OnInit, OnDestroy {

  @Input() isRequiredLegalEntity: boolean;

  @Input() selectedFolderId: number;
  @Input() selectedLegalEntityId: number;
  @Input() selectedTreatmentCategoryId: number;

  @ViewChild('popContentFolder') popContentFolder;
  @ViewChild('popContentLegalEntity') popContentLegalEntity;

  public legalEntity: NavigationData[];
  public folders: NavigationData[];
  public treatmentCategories: NavigationData[];

  public selectedFolder: NavigationData;
  public selectedLegalEntity: NavigationData;

  public formGroup: FormGroup;
  public REQUIRED = DocumentsUtils.ValidatorGenerator.generateWidgetValidator(REQUIRED_VALIDATOR_KEY);

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    private folderService: FolderService,
    private legalEntityService: LegalEntityService
  ) {
  }


  ngOnInit(): void {
    this.initNavigationData();

    this.actionsSubject.pipe(
      ofType(
        FolderActions.createFolderSuccess,
        FolderActions.createFolderFailed
      ),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      switch (action.type) {
        case FolderActions.createFolderSuccess.type:
          this.activeModal.close(true);
          break;
        case FolderActions.createFolderFailed.type:
          break;
      }
    });
  }

  initNavigationData() {
    forkJoin({
      folders: this.folderService?.findAllFolders(),
      legalEntities: this.legalEntityService.getInternalLegalEntities()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: { folders, legalEntities }) => {
      if (response.folders) {
        this.folders = response.folders.map(f => NavigationData.folderToNavigationData(f));
      }
      if (response.legalEntities) {
        this.legalEntity = response.legalEntities.map(l => NavigationData.legalEntityToNavigationData(l));
      }

      this.initFormGroup();
    });
  }

  initFormGroup() {
    if (this.selectedFolderId !== null && this.selectedFolderId !== undefined && this.folders) {
      this.selectedFolder = this.folders?.find((f) => f.id == this.selectedFolderId);
    } else {
      this.selectedFolder = this.folders?.find((f) => f.parentId == null);
    }

    if (this.selectedLegalEntityId !== null && this.selectedLegalEntityId !== undefined && this.legalEntity) {
      this.selectedLegalEntity = this.legalEntity?.find((f) => f.id == this.selectedLegalEntityId);
    }

    this.formGroup = new FormGroup({
      internalId: new FormControl(null),
      description: new FormControl(null),
      name: new FormControl(null, [Validators.required]),
      folder: new FormControl(this.selectedFolder?.name, [Validators.required]),
      legalEntity: new FormControl(this.selectedLegalEntity?.name, this.isRequiredLegalEntity ? [Validators.required] : [])
    });
  }

  confirm() {
    const request = {
      name: this.formGroup.controls.name.value,
      description: this.formGroup.controls.description.value,
      internalId: this.formGroup.controls.internalId.value,
      legalEntityId: this.selectedLegalEntity?.id,
      parentId: this.selectedFolder?.id
    } as FolderCreateRequest;

    this.store.dispatch(FolderActions.createFolder({
      request: request
    }));
  }


  selectFolder(resource: NavigationData) {
    this.popContentFolder.close();
    this.selectedFolder = resource;
    this.formGroup.controls.folder.setValue(this.selectedFolder?.name);
  }

  selectLegalEntity(resource: NavigationData) {
    this.popContentLegalEntity.close();
    this.selectedLegalEntity = resource;
    this.formGroup.controls.legalEntity.setValue(this.selectedLegalEntity?.name);
  }

  chooseFolder() {
    this.popContentFolder.open();
  }

  chooseLegalEntity() {
    this.popContentLegalEntity.open();
  }

  cleanFolder() {
    this.formGroup?.controls?.folder?.setValue(null);
    this.selectedFolder = null;
  }

  cleanLegalEntity() {
    this.formGroup?.controls?.legalEntity?.setValue(null);
    this.selectedLegalEntity = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

}
